import type {
  TournamentEvent,
  TournamentEventPriceUpdated,
  FastForwardResponse,
} from './types'

export type EventHandler<T = unknown> = (payload: T) => void

export type EventType = 'TOURNAMENT_EVENT' | 'PRICE_UPDATED' | 'FAST_FORWARD_RESPONSE' | 'FAST_FORWARD_ERROR' | '*'

export interface EventSubscription {
  unsubscribe: () => void
}

export class EventManager {
  private handlers: Map<string, Set<EventHandler>> = new Map()

  on(
    eventType: 'TOURNAMENT_EVENT',
    handler: EventHandler<TournamentEvent>
  ): EventSubscription
  on(
    eventType: 'PRICE_UPDATED',
    handler: EventHandler<TournamentEventPriceUpdated>
  ): EventSubscription
  on(
    eventType: 'FAST_FORWARD_RESPONSE',
    handler: EventHandler<FastForwardResponse>
  ): EventSubscription
  on(
    eventType: 'FAST_FORWARD_ERROR',
    handler: EventHandler<{ message: string }>
  ): EventSubscription
  on<T>(eventType: EventType, handler: EventHandler<T>): EventSubscription
  on<T>(eventType: EventType, handler: EventHandler<T>): EventSubscription {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set())
    }
    this.handlers.get(eventType)!.add(handler as EventHandler)

    return {
      unsubscribe: () => this.off(eventType, handler),
    }
  }

  off(
    eventType: 'TOURNAMENT_EVENT',
    handler: EventHandler<TournamentEvent>
  ): void
  off(
    eventType: 'PRICE_UPDATED',
    handler: EventHandler<TournamentEventPriceUpdated>
  ): void
  off(
    eventType: 'FAST_FORWARD_RESPONSE',
    handler: EventHandler<FastForwardResponse>
  ): void
  off(
    eventType: 'FAST_FORWARD_ERROR',
    handler: EventHandler<{ message: string }>
  ): void
  off<T>(eventType: EventType, handler: EventHandler<T>): void
  off<T>(eventType: EventType, handler: EventHandler<T>): void {
    const handlers = this.handlers.get(eventType)
    if (handlers) {
      handlers.delete(handler as EventHandler)
      if (handlers.size === 0) {
        this.handlers.delete(eventType)
      }
    }
  }

  emit<T>(eventType: EventType, payload: T): void {
    const handlers = this.handlers.get(eventType)
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(payload)
        } catch (error) {
          console.error(`Error in event handler for ${eventType}:`, error)
        }
      })
    }

    const wildcardHandlers = this.handlers.get('*')
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => {
        try {
          handler(payload)
        } catch (error) {
          console.error(`Error in wildcard event handler:`, error)
        }
      })
    }
  }

  clear(eventType?: EventType): void {
    if (eventType) {
      this.handlers.delete(eventType)
    } else {
      this.handlers.clear()
    }
  }
}
